import { useState, type ChangeEvent, type KeyboardEvent } from "react";
import { useIntl } from "react-intl";

// Inline pill stepper for a single numeric value. ARIA spinbutton with:
//   − click / ↓ key:     decrement by `step`
//   + click / ↑ key:     increment by `step`
//   Home / End:          jump to `min` / `max` (only when bounded)
//   click value + type:  edit directly; commit on blur / Enter
//
// Use for a numeric input where the range is small or open-ended and the
// user wants both quick ±1 adjustments AND the ability to type a larger
// value directly. For "increment/decrement a value displayed elsewhere",
// use two <Button variant="icon"> instead — that's a different pattern.

interface NumberStepperProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}

export function NumberStepper({
  value,
  onChange,
  min = Number.NEGATIVE_INFINITY,
  max = Number.POSITIVE_INFINITY,
  step = 1,
  label,
  disabled = false,
  ariaLabel,
  className = "",
}: NumberStepperProps) {
  const [draft, setDraft] = useState<string | null>(null);

  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  const setClamped = (n: number) => {
    if (Number.isFinite(n)) onChange(clamp(n));
  };

  const handleDecrement = () => setClamped(value - step);
  const handleIncrement = () => setClamped(value + step);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDraft(e.target.value);
  };

  const commitDraft = () => {
    if (draft == null) return;
    if (draft.trim() === "") {
      setDraft(null);
      return;
    }
    // Dezimal-Komma (de) wie Punkt behandeln: "6,5" -> 6.5.
    const parsed = Number(draft.replace(",", "."));
    if (Number.isFinite(parsed)) setClamped(parsed);
    setDraft(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      handleIncrement();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      handleDecrement();
    } else if (e.key === "Home" && Number.isFinite(min)) {
      e.preventDefault();
      setClamped(min);
    } else if (e.key === "End" && Number.isFinite(max)) {
      e.preventDefault();
      setClamped(max);
    } else if (e.key === "Enter") {
      e.preventDefault();
      commitDraft();
      (e.currentTarget as HTMLInputElement).blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setDraft(null);
      (e.currentTarget as HTMLInputElement).blur();
    }
  };

  const canDecrement = !disabled && value > min;
  const canIncrement = !disabled && value < max;
  // Screen-reader labels for the ± buttons. The noun comes from the
  // caller (ariaLabel/label is already localized by the consumer); the
  // verb wrapper is localized here so "Decrease …" / "Increase …"
  // matches the rest of the UI locale rather than mixing English verbs
  // with a German noun.
  const intl = useIntl();
  const noun = ariaLabel ?? label ?? intl.formatMessage({
    id: "design.numberStepperFallbackNoun",
    defaultMessage: "value",
  });
  const decreaseLabel = intl.formatMessage(
    { id: "design.numberStepperDecrease", defaultMessage: "Decrease {noun}" },
    { noun },
  );
  const increaseLabel = intl.formatMessage(
    { id: "design.numberStepperIncrease", defaultMessage: "Increase {noun}" },
    { noun },
  );

  return (
    <div className={className}>
      {label ? (
        <div className="mb-1 text-[10px] uppercase tracking-[0.2em] text-ink/45">{label}</div>
      ) : null}
      <div className="inline-flex items-center overflow-hidden rounded-full ring-1 ring-ink/15 transition focus-within:ring-2 focus-within:ring-ink/30">
        <button
          type="button"
          onClick={handleDecrement}
          disabled={!canDecrement}
          aria-label={decreaseLabel}
          className="px-2.5 py-0.5 text-sm disabled:opacity-30"
        >
          <span aria-hidden="true">−</span>
        </button>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          role="spinbutton"
          aria-label={ariaLabel ?? label}
          aria-valuemin={Number.isFinite(min) ? min : undefined}
          aria-valuemax={Number.isFinite(max) ? max : undefined}
          aria-valuenow={value}
          value={draft ?? String(value)}
          onChange={handleInputChange}
          onBlur={commitDraft}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="w-10 bg-transparent px-1 py-0.5 text-center text-sm font-medium tabular-nums outline-none disabled:opacity-50"
        />
        <button
          type="button"
          onClick={handleIncrement}
          disabled={!canIncrement}
          aria-label={increaseLabel}
          className="px-2.5 py-0.5 text-sm disabled:opacity-30"
        >
          <span aria-hidden="true">+</span>
        </button>
      </div>
    </div>
  );
}
